import { useEffect, useState } from "react";
import { supabase } from "./lib/supabase.js";
import {
  getStatus as getApiFootballStatus,
  getFixtures,
  API_TIER,
  DEFAULT_SEASON,
  LEAGUE_IDS,
  isProTier,
} from "./lib/footballApi.js";
import { getTeamIcon } from "./lib/teamIcons.js";

export function Operator({ onBack }) {
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jsonInput, setJsonInput] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [outcomesRoundId, setOutcomesRoundId] = useState(null);
  const [apiStatus, setApiStatus] = useState(null);
  const [apiChecking, setApiChecking] = useState(false);
  const [apiError, setApiError] = useState(null);

  // Import-from-API state ----------------------------------------------
  const [importLeague, setImportLeague] = useState("PL");
  const [importSeason, setImportSeason] = useState(String(DEFAULT_SEASON));
  const [importFrom, setImportFrom] = useState("");
  const [importTo, setImportTo] = useState("");
  const [importRoundNumber, setImportRoundNumber] = useState("");
  const [importFixtures, setImportFixtures] = useState([]);
  const [importBonusId, setImportBonusId] = useState(null);
  const [importFetching, setImportFetching] = useState(false);
  const [importError, setImportError] = useState(null);

  const handleTestApi = async () => {
    setApiError(null); setApiStatus(null); setApiChecking(true);
    try {
      const s = await getApiFootballStatus();
      setApiStatus(s);
    } catch (e) {
      setApiError(e.message || String(e));
    } finally {
      setApiChecking(false);
    }
  };

  const handleFetchFixtures = async () => {
    setImportError(null);
    setImportFixtures([]);
    setImportBonusId(null);
    setImportFetching(true);
    try {
      const leagueId = LEAGUE_IDS[importLeague];
      if (!leagueId) throw new Error(`Unknown league code: ${importLeague}`);
      const fixtures = await getFixtures({
        league: leagueId,
        season: parseInt(importSeason, 10) || DEFAULT_SEASON,
        from: importFrom || undefined,
        to: importTo || undefined,
      });
      const sorted = (fixtures || []).slice().sort(
        (a, b) => new Date(a.fixture.date) - new Date(b.fixture.date)
      );
      setImportFixtures(sorted);
      if (sorted.length === 0) {
        setImportError(`No fixtures returned. Free tier covers seasons 2022–2024 only — current default season is ${DEFAULT_SEASON}. Try a date range within an active matchday for the chosen league.`);
      }
    } catch (e) {
      setImportError(e.message || String(e));
    } finally {
      setImportFetching(false);
    }
  };

  const handleBuildJson = () => {
    if (!importFixtures.length) return;
    // If first fixture's kickoff is in the past (e.g. historical VM 2022 test data
    // on Free tier), auto-shift all kickoffs forward so they land in the future.
    // This lets you actually pick on them in the lobby. Real future fixtures pass
    // through unchanged.
    const now = Date.now();
    const firstKickoff = new Date(importFixtures[0].fixture.date).getTime();
    const shiftMs = firstKickoff < now
      ? (now - firstKickoff) + 24 * 60 * 60 * 1000  // shift so first kickoff = tomorrow same hour
      : 0;
    const shiftDate = (iso) => shiftMs === 0
      ? iso
      : new Date(new Date(iso).getTime() + shiftMs).toISOString();

    const matches = importFixtures.map((f, i) => {
      const home = f.teams.home.name;
      const away = f.teams.away.name;
      return {
        api_fixture_id: f.fixture.id,
        league_id: importLeague,
        home_team: home,
        away_team: away,
        home_short: shortTeam(home),
        away_short: shortTeam(away),
        kickoff_at: shiftDate(f.fixture.date),
        is_bonus: f.fixture.id === importBonusId,
        sort_order: i,
        // Placeholder odds — Free tier doesn't return odds for historical fixtures.
        // Operator should edit these (or wait for Pro upgrade to auto-populate).
        layer1_options: [
          { label: `${home} wins`, icon: getTeamIcon(home, "home"), odds: 2.00 },
          { label: "Draw", icon: "⚖️", odds: 3.50 },
          { label: `${away} wins`, icon: getTeamIcon(away, "away"), odds: 3.00 },
        ],
        layer2_options: [
          { label: "Over 2.5 mål", icon: "🔥", odds: 1.85 },
          { label: "Under 2.5 mål", icon: "🧊", odds: 2.10 },
          { label: "Begge hold scorer", icon: "⚡", odds: 1.80 },
        ],
        layer3_options: [
          { label: "PLACEHOLDER — edit me", icon: "⚽", odds: 3.00 },
          { label: "PLACEHOLDER — edit me", icon: "🟥", odds: 4.00 },
          { label: "PLACEHOLDER — edit me", icon: "🎯", odds: 3.50 },
        ],
        layer3_reroll: [
          { label: "PLACEHOLDER reroll — edit me", icon: "⚽", odds: 3.20 },
          { label: "PLACEHOLDER reroll — edit me", icon: "🟥", odds: 4.20 },
          { label: "PLACEHOLDER reroll — edit me", icon: "🎯", odds: 3.30 },
        ],
      };
    });

    // Round-level dates also shifted (so locks_at, opens_at, ends_at are sane).
    // opens_at = "tomorrow morning at 08:00 local-ish" (set 1h before first kickoff)
    // locks_at = first match's shifted kickoff
    // ends_at  = last match's shifted kickoff + 4h
    const firstShifted = new Date(matches[0].kickoff_at);
    const lastShifted = new Date(matches[matches.length - 1].kickoff_at);
    const opensAt = new Date(firstShifted.getTime() - 60 * 60 * 1000).toISOString();
    const endsAt = new Date(lastShifted.getTime() + 4 * 60 * 60 * 1000).toISOString();

    const round = {
      number: importRoundNumber ? Number(importRoundNumber) : null,
      title: `${importLeague} ${importFrom}${importTo && importTo !== importFrom ? " – " + importTo : ""}${shiftMs > 0 ? " (test-shifted)" : ""}`,
      opens_at: opensAt,
      locks_at: matches[0].kickoff_at,
      ends_at: endsAt,
      max_picks: Math.min(5, matches.length),
      matches,
    };
    setJsonInput(JSON.stringify(round, null, 2));
    setInfo(
      `Imported ${matches.length} fixtures → JSON ready below.` +
      (shiftMs > 0 ? " Kickoff dates auto-shifted to future for test mode (historical data on Free tier)." : "") +
      " Edit placeholder odds before creating round."
    );
  };

  const loadRounds = async () => {
    const { data, error } = await supabase
      .from("rounds")
      .select("id, number, title, status, opens_at, locks_at, ends_at, matches(count)")
      .order("number", { ascending: false });
    if (error) setError(error.message);
    else setRounds(data || []);
    setLoading(false);
  };
  useEffect(() => { loadRounds(); }, []);

  const handleCreate = async () => {
    setError(null); setInfo(null); setSubmitting(true);
    try {
      const parsed = JSON.parse(jsonInput);
      const { matches: matchInputs = [], ...roundFields } = parsed;
      const { data: round, error: rErr } = await supabase
        .from("rounds")
        .insert({
          number: roundFields.number,
          title: roundFields.title ?? null,
          opens_at: roundFields.opens_at,
          locks_at: roundFields.locks_at,
          ends_at: roundFields.ends_at,
          status: "draft",
          max_picks: roundFields.max_picks ?? 5,
        })
        .select()
        .single();
      if (rErr) throw rErr;
      if (matchInputs.length > 0) {
        const matchRows = matchInputs.map((m, i) => ({
          round_id: round.id,
          league_id: m.league_id,
          home_team: m.home_team, away_team: m.away_team,
          home_short: m.home_short, away_short: m.away_short,
          kickoff_at: m.kickoff_at, is_bonus: !!m.is_bonus,
          sort_order: m.sort_order ?? i,
          layer1_options: m.layer1_options,
          layer2_options: m.layer2_options,
          layer3_options: m.layer3_options,
          layer3_reroll: m.layer3_reroll ?? null,
          stats: m.stats ?? null,
        }));
        const { error: mErr } = await supabase.from("matches").insert(matchRows);
        if (mErr) throw mErr;
      }
      setInfo(`Round ${roundFields.number} created (${matchInputs.length} matches).`);
      setJsonInput("");
      await loadRounds();
    } catch (e) { setError(e.message || String(e)); }
    finally { setSubmitting(false); }
  };

  const transition = async (roundId, newStatus) => {
    setError(null); setInfo(null);
    const { error } = await supabase.from("rounds").update({ status: newStatus }).eq("id", roundId);
    if (error) setError(error.message);
    else { setInfo(`Round status → ${newStatus}.`); loadRounds(); }
  };

  const remove = async (roundId, num) => {
    if (!window.confirm(`Delete round ${num} and all its matches/picks/outcomes?`)) return;
    setError(null); setInfo(null);
    const { error } = await supabase.from("rounds").delete().eq("id", roundId);
    if (error) setError(error.message);
    else { setInfo(`Round ${num} deleted.`); loadRounds(); }
  };

  const handleResolve = async (roundId, num) => {
    if (!window.confirm(`Resolve round ${num}? Det scorer alle picks og uddeler præmier. Kan ikke fortrydes.`)) return;
    setError(null); setInfo(null);
    const { error } = await supabase.rpc("resolve_round", { p_round_id: roundId });
    if (error) setError(error.message);
    else { setInfo(`Round ${num} resolved — points beregnet og prizes uddelt.`); loadRounds(); }
  };

  const nextStatusButton = (r) => {
    if (r.status === "draft") return { label: "Open round", next: "open" };
    if (r.status === "open") return { label: "Lock round", next: "locked" };
    return null;
  };

  if (outcomesRoundId) {
    return (
      <OutcomesView
        roundId={outcomesRoundId}
        onBack={() => { setOutcomesRoundId(null); loadRounds(); }}
        onError={setError}
        onInfo={setInfo}
      />
    );
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn}>← Back to Pickfox</button>
        <div>
          <div style={S.kicker}>OPERATOR</div>
          <div style={S.title}>Manage rounds</div>
        </div>
      </div>
      {error && <div style={S.errorBanner}>⚠ {error}</div>}
      {info && <div style={S.infoBanner}>✓ {info}</div>}
      <section style={S.section}>
        <div style={S.sectionHead}>API-Football {API_TIER === "pro" ? "(Pro)" : "(Free)"}</div>
        <p style={S.muted}>
          Default season: <b>{DEFAULT_SEASON}</b>.{" "}
          {API_TIER === "free" && <>Free tier — kun sæsoner 2022-2024, max 100 requests/dag.</>}
        </p>
        <button onClick={handleTestApi} disabled={apiChecking} style={{ ...S.outlineBtn, padding: "8px 14px", fontSize: 13 }}>
          {apiChecking ? "Tester…" : "Test API-Football"}
        </button>
        {apiError && <div style={{ ...S.errorBanner, marginTop: 10, marginBottom: 0 }}>⚠ {apiError}</div>}
        {apiStatus && (
          <div style={{ marginTop: 10, padding: "10px 12px", borderRadius: 8, background: "#fff", border: "1px solid #ede8df", fontSize: 12, lineHeight: 1.6 }}>
            <div><b>Account:</b> {apiStatus.account?.firstname} {apiStatus.account?.lastname} ({apiStatus.account?.email})</div>
            <div><b>Plan:</b> {apiStatus.subscription?.plan} · active til {apiStatus.subscription?.end}</div>
            <div><b>Requests i dag:</b> {apiStatus.requests?.current} / {apiStatus.requests?.limit_day}</div>
          </div>
        )}
      </section>
      <section style={S.section}>
        <div style={S.sectionHead}>Existing rounds</div>
        {loading && <div style={S.muted}>Loading…</div>}
        {!loading && rounds.length === 0 && <div style={S.muted}>No rounds yet — create the first one below.</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rounds.map(r => {
            const tBtn = nextStatusButton(r);
            const canEnterOutcomes = r.status === "open" || r.status === "locked";
            const canResolve = r.status === "locked";
            return (
              <div key={r.id} style={S.roundRow}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.roundHead}>
                    <span style={S.roundNum}>Round {r.number}</span>
                    <span style={{ ...S.statusPill, ...statusColor(r.status) }}>{r.status}</span>
                  </div>
                  {r.title && <div style={S.roundSub}>{r.title}</div>}
                  <div style={S.roundMeta}>
                    {r.matches?.[0]?.count ?? 0} matches · ends {fmtDate(r.ends_at)}
                  </div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {tBtn && (<button onClick={() => transition(r.id, tBtn.next)} style={S.smallBtn}>{tBtn.label}</button>)}
                  {canEnterOutcomes && (
                    <button onClick={() => setOutcomesRoundId(r.id)} style={S.outlineBtn}>Enter outcomes</button>
                  )}
                  {canResolve && (
                    <button onClick={() => handleResolve(r.id, r.number)} style={S.resolveBtn}>Resolve round</button>
                  )}
                  <button onClick={() => remove(r.id, r.number)} style={S.dangerBtn}>Delete</button>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <section style={S.section}>
        <div style={S.sectionHead}>Import fixtures from API-Football</div>
        <p style={S.muted}>
          Henter ALLE kampe for valgt liga + datointerval ({API_TIER === "pro" ? "Pro" : "Free"} tier, season {DEFAULT_SEASON}). Bygger automatisk en JSON-runde du kan redigere før submit. {!isProTier() && <b>NB: Free tier returnerer ingen odds — Lag 1/2/3 udfyldes med placeholder-odds som du bør rette manuelt før save.</b>}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
          <label style={S.formLabel}>
            League
            <select value={importLeague} onChange={e => setImportLeague(e.target.value)} style={S.formInput}>
              {Object.keys(LEAGUE_IDS).map(code => (
                <option key={code} value={code}>{code}</option>
              ))}
            </select>
          </label>
          <label style={S.formLabel}>
            Season
            <input type="number" min="2022" max="2030" value={importSeason} onChange={e => setImportSeason(e.target.value)} style={S.formInput} />
          </label>
          <label style={S.formLabel}>
            From (YYYY-MM-DD)
            <input type="date" value={importFrom} onChange={e => setImportFrom(e.target.value)} style={S.formInput} />
          </label>
          <label style={S.formLabel}>
            To (YYYY-MM-DD)
            <input type="date" value={importTo} onChange={e => setImportTo(e.target.value)} style={S.formInput} />
          </label>
          <label style={S.formLabel}>
            Round number
            <input type="number" min="1" value={importRoundNumber} onChange={e => setImportRoundNumber(e.target.value)} style={S.formInput} placeholder="e.g. 2" />
          </label>
        </div>
        <button onClick={handleFetchFixtures} disabled={importFetching} style={{ ...S.outlineBtn, padding: "8px 14px", fontSize: 13, marginRight: 8 }}>
          {importFetching ? "Fetching…" : "Fetch fixtures"}
        </button>
        {importFixtures.length > 0 && (
          <button onClick={handleBuildJson} disabled={!importRoundNumber} style={{ ...S.primaryBtn, padding: "8px 14px", fontSize: 13 }}>
            Generate JSON ({importFixtures.length} matches)
          </button>
        )}
        {importError && <div style={{ ...S.errorBanner, marginTop: 10, marginBottom: 0 }}>⚠ {importError}</div>}
        {importFixtures.length > 0 && (
          <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={S.muted}>Click ⭐ to mark a single match as the bonus (1.5× multiplier).</div>
            {importFixtures.map(f => {
              const home = f.teams.home.name;
              const away = f.teams.away.name;
              const isBonus = importBonusId === f.fixture.id;
              return (
                <div key={f.fixture.id} style={{ ...S.roundRow, padding: "8px 12px" }}>
                  <div style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                    <span style={{ marginRight: 6 }}>{getTeamIcon(home, "home")}</span>
                    <b>{home}</b> vs <b>{away}</b>
                    <span style={{ marginLeft: 6 }}>{getTeamIcon(away, "away")}</span>
                    <div style={{ fontSize: 11, color: "#a8a29e", marginTop: 2 }}>{fmtDate(f.fixture.date)}</div>
                  </div>
                  <button
                    onClick={() => setImportBonusId(isBonus ? null : f.fixture.id)}
                    style={{
                      ...S.outlineBtn,
                      background: isBonus ? "#fef3c7" : "#fbf8f1",
                      borderColor: isBonus ? "#fcd34d" : "#ece6d8",
                      color: isBonus ? "#92400e" : "#57534e",
                    }}
                  >
                    {isBonus ? "⭐ Bonus" : "Mark bonus"}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>
      <section style={S.section}>
        <div style={S.sectionHead}>Create new round</div>
        <p style={S.muted}>
          Paste round + matches JSON below, or use Import-flowet ovenfor.{" "}
          <button onClick={() => setJsonInput(TEMPLATE)} style={S.linkBtn}>Use manual template</button>
          {" "}— edit teams, dates, layer options, then submit.
        </p>
        <textarea value={jsonInput} onChange={(e) => setJsonInput(e.target.value)}
          rows={18} spellCheck={false} style={S.textarea}
          placeholder='{"number": 1, "matches": [...]}' />
        <button onClick={handleCreate} disabled={!jsonInput || submitting} style={S.primaryBtn}>
          {submitting ? "Creating…" : "Create round"}
        </button>
      </section>
    </div>
  );
}

function OutcomesView({ roundId, onBack, onError, onInfo }) {
  const [round, setRound] = useState(null);
  const [matches, setMatches] = useState([]);
  const [outcomes, setOutcomes] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data: r, error: rErr } = await supabase
        .from("rounds")
        .select("*, matches(*, match_outcomes(option_label, correct))")
        .eq("id", roundId)
        .single();
      if (cancelled) return;
      if (rErr) { setLocalError(rErr.message); setLoading(false); return; }
      setRound(r);
      const sortedMatches = (r.matches || []).slice().sort((a, b) => a.sort_order - b.sort_order);
      setMatches(sortedMatches);
      const initial = {};
      for (const m of sortedMatches) {
        initial[m.id] = {};
        for (const o of (m.match_outcomes || [])) {
          initial[m.id][o.option_label] = !!o.correct;
        }
      }
      setOutcomes(initial);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [roundId]);

  const toggleOption = (matchId, optionLabel) => {
    setOutcomes(prev => ({
      ...prev,
      [matchId]: { ...prev[matchId], [optionLabel]: !prev[matchId]?.[optionLabel] },
    }));
  };

  const handleSave = async () => {
    setSaving(true); setLocalError(null);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not signed in");
      const rows = [];
      for (const m of matches) {
        const allOptions = collectAllOptionLabels(m);
        for (const label of allOptions) {
          rows.push({
            match_id: m.id,
            option_label: label,
            correct: !!outcomes[m.id]?.[label],
            entered_by: user.id,
            entered_at: new Date().toISOString(),
          });
        }
      }
      if (rows.length === 0) { onInfo("No options to save."); return; }
      const { error } = await supabase
        .from("match_outcomes")
        .upsert(rows, { onConflict: "match_id,option_label" });
      if (error) throw error;
      onInfo(`Outcomes saved (${rows.length} options across ${matches.length} matches).`);
      onBack();
    } catch (e) {
      setLocalError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={S.wrap}><div style={S.muted}>Loading round outcomes…</div></div>;
  }

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <button onClick={onBack} style={S.backBtn}>← Back</button>
        <div>
          <div style={S.kicker}>OUTCOMES · ROUND {round?.number}</div>
          <div style={S.title}>Markér hvad der skete</div>
        </div>
      </div>
      <p style={{ ...S.muted, marginBottom: 14 }}>
        Tik den option der skete for hver layer. Utikkede options registreres som "ikke korrekt".
        Når alle outcomes er sat, gå tilbage og klik <b>Resolve round</b> for at score runden.
      </p>
      {localError && <div style={S.errorBanner}>⚠ {localError}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {matches.map(m => (
          <MatchOutcomeCard
            key={m.id}
            match={m}
            outcomes={outcomes[m.id] || {}}
            onToggle={(label) => toggleOption(m.id, label)}
          />
        ))}
      </div>
      <button onClick={handleSave} disabled={saving} style={{ ...S.primaryBtn, width: "100%", marginTop: 16 }}>
        {saving ? "Saving…" : "Save outcomes"}
      </button>
    </div>
  );
}

function MatchOutcomeCard({ match, outcomes, onToggle }) {
  const layers = [
    { label: "Layer 1 — Match result", options: match.layer1_options || [] },
    { label: "Layer 2 — Goals in match", options: match.layer2_options || [] },
    { label: "Layer 3 — Risky prop", options: match.layer3_options || [] },
  ];
  if (match.layer3_reroll) {
    layers.push({ label: "Layer 3 reroll (alt)", options: match.layer3_reroll });
  }
  return (
    <div style={S.section}>
      <div style={{ fontSize: 11, color: "#a8a29e", letterSpacing: 1.2, fontWeight: 600, marginBottom: 4 }}>
        {match.league_id} · {fmtDate(match.kickoff_at)}{match.is_bonus && " · ⭐ bonus 1.5×"}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>
        {match.home_team} <span style={{ color: "#a8a29e" }}>vs</span> {match.away_team}
      </div>
      {layers.map(layer => (
        <div key={layer.label} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: "#78716c", fontWeight: 600, marginBottom: 6, letterSpacing: 0.4 }}>
            {layer.label}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {layer.options.map(opt => {
              const checked = !!outcomes[opt.label];
              return (
                <label key={opt.label} style={{
                  display: "flex", alignItems: "center", gap: 10,
                  padding: "8px 10px", borderRadius: 8,
                  background: checked ? "#dcfce7" : "#fff",
                  border: `1px solid ${checked ? "#86efac" : "#ede8df"}`,
                  cursor: "pointer", fontSize: 13,
                }}>
                  <input type="checkbox" checked={checked} onChange={() => onToggle(opt.label)}
                    style={{ width: 16, height: 16, accentColor: "#16a34a", cursor: "pointer" }} />
                  {opt.icon && <span>{opt.icon}</span>}
                  <span style={{ flex: 1, fontWeight: checked ? 600 : 500, color: checked ? "#166534" : "#1c1917" }}>
                    {opt.label}
                  </span>
                  {opt.odds && <span style={{ fontSize: 11, color: "#a8a29e" }}>{opt.odds}×</span>}
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function collectAllOptionLabels(match) {
  const labels = new Set();
  const sources = [match.layer1_options, match.layer2_options, match.layer3_options, match.layer3_reroll];
  for (const src of sources) {
    if (!src) continue;
    for (const opt of src) {
      if (opt?.label) labels.add(opt.label);
    }
  }
  return Array.from(labels);
}

const TEMPLATE = JSON.stringify({
  number: 1, title: "First Pickfox round",
  opens_at: "2026-05-05T10:00:00+02:00",
  locks_at: "2026-05-09T15:00:00+02:00",
  ends_at: "2026-05-10T22:00:00+02:00",
  max_picks: 5,
  matches: [{
    league_id: "PL", home_team: "Arsenal", away_team: "Tottenham",
    home_short: "ARS", away_short: "TOT", kickoff_at: "2026-05-09T15:00:00+02:00",
    is_bonus: true,
    layer1_options: [
      { label: "1", icon: "🏠", odds: 1.85 },
      { label: "X", icon: "⚖️", odds: 3.40 },
      { label: "2", icon: "✈️", odds: 4.20 }
    ],
    layer2_options: [
      { label: "Over 2.5 mål", icon: "⚽", odds: 1.65 },
      { label: "Under 2.5 mål", icon: "🛡️", odds: 2.20 }
    ],
    layer3_options: [
      { label: "Saka scorer", icon: "🎯", odds: 2.50 },
      { label: "Begge hold scorer", icon: "🔁", odds: 1.55 },
      { label: "Rødt kort i kampen", icon: "🟥", odds: 5.00 }
    ]
  }]
}, null, 2);

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("da-DK", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

const statusColor = (s) => ({
  draft:    { background: "#f5f5f4", color: "#57534e", borderColor: "#d6d3d1" },
  open:     { background: "#fef3c7", color: "#92400e", borderColor: "#fcd34d" },
  locked:   { background: "#dbeafe", color: "#1e3a8a", borderColor: "#93c5fd" },
  resolved: { background: "#dcfce7", color: "#166534", borderColor: "#86efac" },
}[s] || {});

const S = {
  wrap: { minHeight: "100vh", background: "#f5f1e8", padding: 18, fontFamily: "system-ui, -apple-system, 'Segoe UI', sans-serif", color: "#1c1917" },
  header: { display: "flex", alignItems: "center", gap: 14, marginBottom: 16 },
  backBtn: { background: "#fbf8f1", border: "1px solid #ece6d8", borderRadius: 8, padding: "7px 11px", fontSize: 12, fontWeight: 600, color: "#57534e", cursor: "pointer", fontFamily: "inherit" },
  kicker: { fontSize: 11, letterSpacing: 1.4, color: "#a8a29e", fontWeight: 600 },
  title: { fontSize: 22, fontWeight: 700 },
  errorBanner: { background: "#fef2f2", color: "#991b1b", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: "1px solid #fecaca", marginBottom: 12 },
  infoBanner: { background: "#dcfce7", color: "#166534", padding: "10px 12px", borderRadius: 8, fontSize: 13, border: "1px solid #86efac", marginBottom: 12 },
  section: { background: "#fbf8f1", border: "1px solid #ece6d8", borderRadius: 14, padding: 16, marginBottom: 14 },
  sectionHead: { fontSize: 11, letterSpacing: 1.2, color: "#a8a29e", fontWeight: 600, textTransform: "uppercase", marginBottom: 10 },
  muted: { color: "#78716c", fontSize: 13, lineHeight: 1.5 },
  roundRow: { display: "flex", alignItems: "stretch", gap: 12, padding: "12px 14px", borderRadius: 10, background: "#fff", border: "1px solid #ede8df" },
  roundHead: { display: "flex", alignItems: "center", gap: 8, marginBottom: 2 },
  roundNum: { fontSize: 15, fontWeight: 700 },
  statusPill: { fontSize: 10, fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", padding: "2px 7px", borderRadius: 6, border: "1px solid" },
  roundSub: { fontSize: 12, color: "#57534e", marginBottom: 2 },
  roundMeta: { fontSize: 11, color: "#a8a29e" },
  smallBtn: { background: "#1c1917", color: "#fbf8f1", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" },
  outlineBtn: { background: "#fbf8f1", color: "#57534e", border: "1px solid #ece6d8", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" },
  resolveBtn: { background: "#16a34a", color: "#fff", border: "none", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap", fontFamily: "inherit" },
  dangerBtn: { background: "transparent", color: "#dc2626", border: "1px solid #fecaca", borderRadius: 7, padding: "6px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  textarea: { width: "100%", boxSizing: "border-box", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 12, lineHeight: 1.5, color: "#1c1917", background: "#fff", border: "1px solid #d6cfbf", borderRadius: 10, padding: 12, outline: "none", resize: "vertical", marginBottom: 10 },
  primaryBtn: { background: "#d97706", color: "#fff", border: "none", borderRadius: 10, padding: "10px 16px", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  linkBtn: { background: "transparent", border: "none", padding: 0, cursor: "pointer", color: "#b45309", fontWeight: 600, fontSize: 13, fontFamily: "inherit", textDecoration: "underline" },
  formLabel: { display: "flex", flexDirection: "column", gap: 4, fontSize: 11, fontWeight: 600, color: "#57534e", letterSpacing: 0.4, textTransform: "uppercase" },
  formInput: { padding: "8px 10px", borderRadius: 8, border: "1px solid #d6cfbf", background: "#fff", fontSize: 13, color: "#1c1917", outline: "none", fontFamily: "inherit" },
};

// 3-letter short code for team display. Used by Import flow when API doesn't
// provide an abbreviated name (which is most of the time).
function shortTeam(name) {
  if (!name) return "???";
  const cleaned = name.replace(/\s+(FC|FF|IF|CF|AS|SC|SE)\.?$/i, "").trim();
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return parts.map(p => p[0]).join("").slice(0, 3).toUpperCase();
}
